import dayjs from "dayjs";
import {debounce} from "lodash";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {toast} from "sonner";
import {CVV_REGEX, EMAIL_REGEX} from "../../../../constants";
import {toCardExpirationDate} from "../../../../helpers";
import {Button, Checkbox, Colors, Container, FormControl, FormControlLabel, LoadingButton} from "../../../../ui";
import {Typography} from "@mui/material";
import {FormHelperText, IconCreditCard, InputAdornment, TextField} from "../../../bounce-ui";
import {PaymentsImages} from "../../../components";
import {PaymentDetails, PaymentType} from "../../../declarations";
import {TermsType} from "../terms-and-conditions/components";
import {TermsAndConditionsApproval} from "../terms-and-conditions/TermsAndConditionsApproval";
import {PaymentContext} from "../utils";
import {fillDevCardForm} from "./dev/devAutofill";
import {DevAutofillBar} from "./dev/DevAutofillBar";
import {LargePaymentSplitLabel} from "./LargePaymentsSplitLabel";
import {OneCentAuthorizationLabel} from "./OneCentAuthorizationLabel";
import {CardFormValues, PaymentFormCommonProps} from "./types";
import {blockAlphabeticInput, buildCardDefaults, convertCardFormToPaymentDetails, formatInput} from "./utils";
import {BinCardType} from "../../../../enums/payments";

const MONTHS_IN_YEAR = 12;
const EXPIRATION_YEARS_AHEAD = 15;
const EXPIRATION_MONTH_OPTIONS = Array.from({length: MONTHS_IN_YEAR}, (_, index) =>
  dayjs().startOf("year").add(index, "month").format("MM")
);
const EXPIRATION_YEAR_OPTIONS = Array.from({length: EXPIRATION_YEARS_AHEAD + 1}, (_, index) =>
  dayjs().add(index, "year").format("YYYY")
);

interface CreditCardPaymentFormProps extends PaymentFormCommonProps {
  type: PaymentType;
  onCardSubmit: (paymentType: PaymentType, paymentDetails: PaymentDetails, isChangePaymentMethod: boolean) => void;
  submitLabelId?: string;
  termsAndConditionsId?: string;
  isChangePaymentMethod: boolean;
  primaryEmail?: string;
  userName?: string;
  showEmailField?: boolean;
  backToDashboardButton?: boolean;
  handleBackToDashboard?: () => void;
  paymentDetails?: PaymentDetails;
  showCredit?: boolean | "";
  accountId?: string;
  accountFlexUIState?: any;
  saveFlexPaymentDetails?: (paymentDetails: PaymentDetails) => void;
  checkBin?: (bin: string) => Promise<"CREDIT" | "DEBIT" | null>;
  blockCreditCards?: boolean;
}

export const DebitPaymentForm = ({
  type,
  onCardSubmit,
  submitLabelId,
  termsAndConditionsId,
  isChangePaymentMethod,
  primaryEmail,
  userName,
  showEmailField = true,
  backToDashboardButton = false,
  handleBackToDashboard,
  paymentDetails,
  pageClickEvent,
  pageViewEvent,
  saveFlexPaymentDetails,
  accountFlexUIState,
  Pages,
  PaymentTermsAndConditionsV2,
  TermsAndConditionsDialog,
  partnerDetails,
  fpPartnerName,
  checkBin,
  blockCreditCards,
}: CreditCardPaymentFormProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const flexStatePaymentDetails = accountFlexUIState?.payment_plan.payment_details;
  const defaultValues = buildCardDefaults(flexStatePaymentDetails ?? paymentDetails, userName, primaryEmail);

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: {isValid},
  } = useForm<CardFormValues>({
    defaultValues,
  });

  const {buttonTouched, setButtonTouched, isLoading, showOneCentLabel, showLargeSplitLabel, disableButton} =
    useContext(PaymentContext);
  const [cardInput, setCardInput] = useState<string>(formatInput(defaultValues?.cardNumber) || "");
  const [binError, setBinError] = useState<string | null>(null);

  const debouncedCheckBin = useMemo(
    () =>
      debounce((bin: string) => {
        checkBin?.(bin)
          .then((cardType) => {
            if (cardType === BinCardType.CREDIT) {
              setBinError("This is a credit card. Enter a debit card instead.");
            }
          })
          .catch(() => {});
      }, 300),
    [checkBin]
  );

  const handleCardChange = (event: any) => {
    const {value} = event.target;
    const numericValue = value.replace(/\D/g, "");
    if (numericValue.length <= 16) {
      setCardInput(formatInput(numericValue));
      setBinError(null);
      if (blockCreditCards && checkBin && numericValue.length >= 6) {
        debouncedCheckBin(numericValue.slice(0, 6));
      }
    }
  };

  const onSubmit = handleSubmit(async (data): Promise<void> => {
    const cardNumber = cardInput.replace(/[\s-]/g, "");
    if (cardNumber.length === 16) {
      setButtonTouched(true);
      const paymentDetails = convertCardFormToPaymentDetails(data, cardNumber);
      saveFlexPaymentDetails?.(paymentDetails);
      onCardSubmit(type, paymentDetails, isChangePaymentMethod);
    } else {
      toast.error("Your credit card number is invalid");
    }
  });

  const handleSaveForm = () => {
    const formValues = getValues();
    if (formValues) {
      const cardNumber = cardInput.replace(/[\s-]/g, "");
      const paymentDetails = convertCardFormToPaymentDetails(formValues, cardNumber);
      saveFlexPaymentDetails?.(paymentDetails);
    }
  };

  useEffect(() => {
    if (saveFlexPaymentDetails) {
      const handleMotion = (e: MouseEvent | KeyboardEvent) => {
        if (
          (e instanceof MouseEvent && ref.current?.contains(e.target as Node)) ||
          (e instanceof KeyboardEvent && e.key === "Tab")
        ) {
          handleSaveForm();
        }
      };
      document.addEventListener("click", handleMotion);
      document.addEventListener("keydown", handleMotion);
      return () => {
        document.removeEventListener("click", handleMotion);
        document.removeEventListener("keydown", handleMotion);
      };
    }
  }, [cardInput]);

  return (
    <div ref={ref} style={{width: "100%", height: "100%"}}>
      <DevAutofillBar onFill={() => fillDevCardForm({setValue, getValues, setCardInput})} />
      <form autoComplete="off" noValidate onSubmit={onSubmit}>
        <Container sx={{gap: "20px"}}>
          {showOneCentLabel && <OneCentAuthorizationLabel type={type} />}
          {showLargeSplitLabel && <LargePaymentSplitLabel />}
          <Controller
            name="cardNumber"
            control={control}
            render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
              return (
                <>
                  <TextField
                    {...field}
                    value={cardInput}
                    onChange={handleCardChange}
                    inputRef={ref}
                    fullWidth
                    label={"Card number"}
                    error={invalid || !!binError}
                    placeholder={"xxxx-xxxx-xxxx-xxxx"}
                    helperText={error?.message}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconCreditCard color={Colors.grey._600} />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      maxLength: 80,
                      inputMode: "numeric",
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        alignItems: "center",
                      },
                    }}
                  />
                  {binError && (
                    <Typography variant="caption" color="error">
                      {binError}
                    </Typography>
                  )}
                </>
              );
            }}
          />
          <Controller
            name="name"
            rules={{
              required: "Required field",
            }}
            control={control}
            render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
              return (
                <TextField
                  {...field}
                  inputRef={ref}
                  fullWidth
                  label={"Cardholder name"}
                  type="text"
                  error={invalid}
                  helperText={error?.message}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    maxLength: 80,
                  }}
                />
              );
            }}
          />
          <Container fullWidth row sx={{gap: "16px"}}>
            <Controller
              name="expirationMonth"
              rules={{
                required: "Required field",
                validate: (month) =>
                  !toCardExpirationDate(month, getValues("expirationYear")).isBefore(dayjs(), "month") ||
                  "Select a valid expiry date",
              }}
              control={control}
              render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
                return (
                  <TextField
                    {...field}
                    inputRef={ref}
                    fullWidth
                    select
                    SelectProps={{native: true}}
                    label={"Expiration month"}
                    error={invalid}
                    helperText={error?.message}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  >
                    <option value="" disabled>
                      MM
                    </option>
                    {EXPIRATION_MONTH_OPTIONS.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </TextField>
                );
              }}
            />
            <Controller
              name="expirationYear"
              rules={{
                required: "Required field",
                deps: "expirationMonth",
              }}
              control={control}
              render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
                return (
                  <TextField
                    {...field}
                    inputRef={ref}
                    fullWidth
                    select
                    SelectProps={{native: true}}
                    label={"Expiration year"}
                    error={invalid}
                    helperText={error?.message}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  >
                    <option value="" disabled>
                      YYYY
                    </option>
                    {EXPIRATION_YEAR_OPTIONS.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </TextField>
                );
              }}
            />
          </Container>
          <Container fullWidth row sx={{gap: "16px"}}>
            <Controller
              name="cvv"
              rules={{
                required: "Required field",
                pattern: {value: CVV_REGEX, message: "CVV must be 3-4 digits"},
              }}
              control={control}
              render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
                return (
                  <TextField
                    {...field}
                    inputRef={ref}
                    fullWidth
                    label={"CVV"}
                    placeholder={"XXX"}
                    error={invalid}
                    helperText={error?.message}
                    onKeyDown={(e) => blockAlphabeticInput(e, 4)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      maxLength: 4,
                      inputMode: "numeric",
                    }}
                  />
                );
              }}
            />
            <Controller
              name="zip"
              rules={{
                required: "Required field",
              }}
              control={control}
              render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
                return (
                  <TextField
                    {...field}
                    inputRef={ref}
                    fullWidth
                    label={"Zip code"}
                    placeholder={"XXXXX"}
                    error={invalid}
                    helperText={error?.message}
                    onKeyDown={(e) => blockAlphabeticInput(e, 5)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      maxLength: 5,
                      inputMode: "numeric",
                    }}
                  />
                );
              }}
            />
          </Container>
          {showEmailField && (
            <Controller
              name="email"
              rules={{
                pattern: {value: EMAIL_REGEX, message: "Email invalid"},
              }}
              control={control}
              render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
                return (
                  <TextField
                    {...field}
                    inputRef={ref}
                    fullWidth
                    label={"Email address for receipts"}
                    type="email"
                    error={invalid}
                    helperText={error?.message}
                    inputProps={{
                      maxLength: 50,
                    }}
                  />
                );
              }}
            />
          )}
          <PaymentsImages />
          <Controller
            name="confirmTerms"
            rules={{
              required: "Required field",
            }}
            control={control}
            render={({field: {ref, value, ...field}, fieldState: {invalid, error}}) => {
              return (
                <FormControl required error={invalid}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        inputRef={ref}
                        size={"small"}
                        checked={value}
                        sx={{alignSelf: "flex-start", marginTop: "0px"}}
                      />
                    }
                    label={
                      <TermsAndConditionsApproval
                        termsAndConditionsId={termsAndConditionsId || TermsType.PAYMENT}
                        page={Pages.PAYMENT_SUMMARY}
                        pageClickEvent={pageClickEvent}
                        pageViewEvent={pageViewEvent}
                        page_T_C={Pages.T_C}
                        TermsAndConditionsDialog={TermsAndConditionsDialog}
                        PaymentTermsAndConditionsV2={PaymentTermsAndConditionsV2}
                        partnerDetails={partnerDetails}
                        fpPartnerName={fpPartnerName}
                        sx={{textAlign: "start"}}
                      />
                    }
                    sx={{alignItems: "flex-start"}}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              );
            }}
          />
          <Container fullWidth sx={{gap: "8px"}}>
            <LoadingButton
              disabled={buttonTouched || disableButton || !isValid || !!binError}
              loading={isLoading}
              type={"submit"}
            >
              {submitLabelId || "Confirm payment"}
            </LoadingButton>
            {backToDashboardButton && (
              <Button buttonType={"contained-secondary"} onClick={handleBackToDashboard} fullWidth>
                Back to dashboard
              </Button>
            )}
          </Container>
        </Container>
      </form>
    </div>
  );
};
